import { RefObject, useMemo, useRef } from 'react'

const MARKDOWN_IDENTIFIER = '\\markdown '

/**
 * convertToMarkdownString - converts a given string into a string which will be interpreted as markdown.
 * If would already be interpreted as markdown, this will do nothing
 *
 * @param {string} maybeMarkdown - a string which should be converted into being interpreted as markdown
 *
 * @return {string} - a string which will be interpreted as markdown
 */
export function convertToMarkdownString(maybeMarkdown: string): string {
	if (isMarkdownString(maybeMarkdown)) {
		return maybeMarkdown
	}
	return MARKDOWN_IDENTIFIER + maybeMarkdown
}

/**
 * useMarkdownString - get the markdown from the given string. If the given string does not represent markdown, return null
 *
 * @param {string} maybeMarkdown - a string which may or may not represent markdown
 *
 * @return {string | null} - a markdown string or null if the given string did not represent markdown
 */
export function getMarkdownString(maybeMarkdown: string): string | null {
	if (!isMarkdownString(maybeMarkdown)) {
		return null
	}
	return (
		// NOTE: a leading space is added due to a bug in markdown-to-jsx
		' ' +
		maybeMarkdown.substring(MARKDOWN_IDENTIFIER.length, maybeMarkdown.length)
	)
}

/**
 * escapeMarkdownCharacters - escapes the markdown control characters so a non-markdown string can be correctly displayed if interpreted as markdown
 *
 * @param nonMarkdown - the string to escape the control characters in
 *
 * @returns string
 */
export function escapeMarkdownCharacters(nonMarkdown: string): string {
	return nonMarkdown
		.replaceAll('\\', '\\\\')
		.replaceAll('*', '\\*')
		.replaceAll('<', '\\<')
		.replaceAll('>', '\\>')
		.replaceAll('#', '\\#')
		.replaceAll('-', '\\-')
		.replaceAll('=', '\\=')
		.replaceAll('`', '\\`')
		.replaceAll('_', '\\_')
}

/**
 * useMarkdownString - get the markdown from the given string. If the given string does not represent markdown, return null
 *
 * @param maybeMarkdown  - a string which may or may not represent markdown
 *
 * @returns - a markdown string or null if the given string did not represent markdown
 */
export function useMarkdownString(maybeMarkdown: string): string | null {
	return useMemo(() => {
		return getMarkdownString(maybeMarkdown)
	}, [maybeMarkdown])
}

/**
 * isMarkdownString - check if a string represents a markdown string
 *
 * @param {string} maybeMarkdown - a string which may or may not be markdown
 *
 * @return {boolean} - true if the string represents markdown, false otherwise
 */
export function isMarkdownString(maybeMarkdown: string): boolean {
	return maybeMarkdown.startsWith(MARKDOWN_IDENTIFIER)
}

type EventTracker = {
	firstEventTypeHandledInCurrentEvaluationLoop: string | null
	previouslyHandledEventTypes: string[]
	noteEventType: (eventType: string) => unknown
}

export type EventTrackerRef = RefObject<EventTracker>

/**
 * useEventTracker - get the reference to an object used to track which events have been handled. This
 * is needed to make sure we do not handle the same event in multiple places. The browser tries to be friendly
 * by emitting the equivalent mouse events after emitting their corresponding touch events when the user is using touch screens,
 * this can usually be prevented by using `preventDefault` and `preventPropagation`, but not always. For example, if a user taps the
 * screen quickly enough that it would be considered a 'click', the following events will always be emitted on touch up : `touchUp`,
 * `mouseDown`, `mouseUp`, `click` (in that order). As such, it is necessary to manually keep track which events have been handled in the
 * current event loop.
 *
 * @return {EventTrackerRef} - an object that tracks the events that are being handled in the current event loop
 */
export function useEventTracker(): EventTrackerRef {
	const ref = useRef<EventTracker>({
		firstEventTypeHandledInCurrentEvaluationLoop: null,
		previouslyHandledEventTypes: [],
		noteEventType: (eventType: string) => {
			ref.current.previouslyHandledEventTypes.push(eventType)

			if (ref.current.firstEventTypeHandledInCurrentEvaluationLoop == null) {
				ref.current.firstEventTypeHandledInCurrentEvaluationLoop = eventType

				setTimeout(() => {
					// clear previously seen on next event loop evaluation
					ref.current.firstEventTypeHandledInCurrentEvaluationLoop = null
					ref.current.previouslyHandledEventTypes = []
				}, 0)
			}
		},
	})

	return ref
}
